import BaseNMComDataObject from "../objects/base.nmcom_data.object";
import BaseModel from "../models/base.model";
import NMComErrorResponse from "../responses/nmcom_error.response";
import xhr from "xhr";
import { newRelicJSError } from "../analytics/new_relic_helper";

const GET = "get";
const PUT = "put";
const POST = "post";
const DELETE = "delete";
const REQUEST_METHODS = [GET, PUT, POST, DELETE];

export default class BaseNMComAdapter {
	static GET = GET;
	static PUT = PUT;
	static POST = POST;
	static DELETE = DELETE;
	static REQUEST_METHODS = REQUEST_METHODS;

	static makeUrlComponents(object, method = GET) {
		if (!(object instanceof BaseNMComDataObject)) {
			throw new Error("object must be an instanceof BaseNMComDataObject");
		}

		const pieces = [];
		let extension = undefined;
		let objectEndpoint = undefined;

		const objectEndpointPieces = (object.endpoint || "").toString().split(".");

		if (objectEndpointPieces.length > 1) {
			extension = objectEndpointPieces.pop();
			objectEndpoint = objectEndpointPieces.join(".");
		} else {
			objectEndpoint = objectEndpointPieces[0];
		}


		if (object instanceof BaseModel) {
			if ((method === GET || method === PUT || method === DELETE) && object.id !== undefined) {
				pieces.unshift(object.id);
			}
		}

		pieces.unshift(objectEndpoint);

		let parent = object.parent;

		while (parent) {
			let parentEndpointPieces = (parent.endpoint || "").toString().split(".");

			pieces.unshift(parent.id);

			if (parentEndpointPieces.length > 1) {
				parentEndpointPieces.pop(); // Remove the extension
			}

			pieces.unshift(parentEndpointPieces.join("."));

			parent = parent.parent;
		}

		return { pieces, extension };
	}

	static serializeRequestData(data) {
		return data;
	}

	static transformResponseData(data) {
		return data;
	}

	static standardizeOptions(object, options = {}) {
		const standardizedOptions = {};

		standardizedOptions['method'] = options['method'] || GET;
		standardizedOptions['fillMethod'] = options['fillMethod'];
		standardizedOptions['params'] = Object.assign({}, options['params'], (object || {}).params);
		standardizedOptions['json'] = true;

		if ('url' in options) {
			standardizedOptions['url'] = options['url'];
		} else {
			const endpoint = (options['endpoint'] || object.makeUrl(options['method']) || "").replace(/^\/+/gi, "");
			const baseUrl = (options['baseUrl'] || window.location.origin || "").replace(/\/+$/gi, "");
			standardizedOptions['url'] = `${baseUrl}/${endpoint}`;
		}

		if (REQUEST_METHODS.indexOf(standardizedOptions['method']) === -1) {
			console.warn(`Invalid request method '${standardizedOptions['method']}' given. Defaulting to GET`);
			standardizedOptions['method'] = GET;
		}

		if (standardizedOptions['method'] === POST || standardizedOptions['method'] === PUT) {
			if (object instanceof BaseModel) {
				standardizedOptions['body'] = options['data'] || object.serialize(standardizedOptions['properties'])
				standardizedOptions['body'] = object.constructor.serializeRequestData(standardizedOptions['body']);
			} else {
				standardizedOptions['body'] = {};
			}

			standardizedOptions['body'] = this.serializeRequestData(standardizedOptions['body']);
		}

		// Work on appending the query string parameters to the url
		let queryStringPieces = [];
		for (let key in standardizedOptions['params']) {
			queryStringPieces.push(`${key}=${standardizedOptions['params'][key]}`)
		}

		if (queryStringPieces.length > 0) {
			standardizedOptions['url'] += (standardizedOptions['url'].indexOf("?") > -1) ? "&" : "?";
			standardizedOptions['url'] += queryStringPieces.join("&");
		}

		return standardizedOptions;
	}

	static promiseSync(object, options = {}) {
		if (!(object instanceof BaseNMComDataObject)) {
			console.warn("Given object is not an NMCom Model or Collection: ");
			console.warn(object);
		}

		options = this.standardizeOptions(object, options);

		return new Promise((resolve, reject) => {
			xhr(options, (error, response, body) => {
				if (error) {
					newRelicJSError(error ? error.error || error.message : "");
					return reject(NMComErrorResponse.generate(error, response));
				}

				if (response.statusCode >= 400) {
					newRelicJSError(error ? error.error || error.message : "");
					return reject(NMComErrorResponse.generate(error, response));
				}

				try {
					let data = this.transformResponseData(body);
					data = object.constructor.transformResponseData(data);

					object.fill(data, options['fillMethod']);

					this.receiveResponse(response, object, options);

					if(object instanceof BaseNMComDataObject) {
						object.receiveResponse(response, options);
					}

					resolve(object);
				} catch (error) {
					newRelicJSError(error);
					reject(NMComErrorResponse.generate(error, response));
				}
			});
		});
	}

	static promiseRPC(endpoint, options = {}) {
		options = Object.assign({}, options);
		options['endpoint'] = endpoint;
		options = this.standardizeOptions(null, options);

		return new Promise((resolve, reject) => {
			xhr(options, (error, response, body) => {
				if (error) {
					reject(error);
				} else if (response.statusCode < 200 || response.statusCode > 299) {
					reject(NMComErrorResponse(response.statusCode, body));
				} else {
					try {
						resolve(body);
					} catch (error) {
						reject(NMComErrorResponse(response.statusCode, body));
					}
				}
			});
		})
	}

	static receiveResponse(response, object, options) { }

	static promiseFetch(object, options = {}) {
		options['method'] = GET;
		return this.promiseSync(object, options);
	}

	static promiseCreate(object, options = {}) {
		options['method'] = POST;
		return this.promiseSync(object, options);
	}

	static promiseSave(object, options = {}) {
		options['method'] = PUT;
		return this.promiseSync(object, options);
	}

	static promiseDestroy(object, options = {}) {
		options['method'] = DELETE;
		return this.promiseSync(object, options);
	}
}
